import { useRef, useState } from "react";
import { ImagePlus, LoaderCircle } from "lucide-react";
import { getDownloadURL, ref, uploadBytesResumable } from "@firebase/storage";
import { storage } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useAccount } from "../lib/account";
import { saveDemoMedia } from "../lib/demo-media";

export function ImageUpload({ siteId, label, onUploaded, disabled = false, lang = "el" }: { siteId: string; label: string; onUploaded(url: string): void; disabled?: boolean; lang?: "el" | "en" }) {
  const { demo } = useAuth();
  const { data } = useAccount();
  const canUpload = data?.access.canEdit === true;
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState("");

  async function upload(file?: File) {
    if (!file) return;
    if (disabled || !canUpload) return;
    if (!file.type.match(/^image\/(jpeg|png|webp|gif)$/) || file.size > 8 * 1024 * 1024) {
      setError(lang === "el" ? "JPG, PNG, WebP ή GIF έως 8MB" : "JPG, PNG, WebP or GIF up to 8MB"); return;
    }
    if (demo) {
      try { const media = await saveDemoMedia(file); onUploaded(media.url); setError(""); if (input.current) input.current.value = ""; }
      catch (cause) { setError(cause instanceof Error ? cause.message : "Local image storage failed."); }
      return;
    }
    if (!storage) { setError(lang === "el" ? "Το Firebase Storage δεν έχει ρυθμιστεί ακόμη." : "Firebase Storage has not been configured yet."); return; }
    setError(""); setProgress(0);
    const safeName = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
    const task = uploadBytesResumable(ref(storage, `sites/${siteId}/${crypto.randomUUID()}-${safeName}`), file, { contentType: file.type, cacheControl: "public,max-age=31536000" });
    task.on("state_changed", snapshot => setProgress(Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100)), reason => { setError(reason.message); setProgress(undefined); }, async () => {
      try { onUploaded(await getDownloadURL(task.snapshot.ref)); if (input.current) input.current.value = ""; }
      catch (reason) { setError(reason instanceof Error ? reason.message : "Upload failed"); }
      finally { setProgress(undefined); }
    });
  }

  return <div className="image-upload">
    <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={event => void upload(event.target.files?.[0])}/>
    <button type="button" onClick={() => input.current?.click()} disabled={disabled || !canUpload || progress !== undefined}>{progress !== undefined ? <LoaderCircle className="spinning"/> : <ImagePlus/>}<span>{progress !== undefined ? `${progress}%` : label}</span></button>
    {error && <small>{error}</small>}
  </div>;
}
